<template>
  <div>
    <confirm-modal
      v-if="showExcelDownloadModal"
      id="excel-download-modal"
      title="Information"
      :confirmation="confirmModalMessage"
      ok-button="Close"
      close-button=""
      @submit="showExcelDownloadModal = false"
    />
    <edit-parcel-items
      v-if="selectedDeclaration"
      id="edit-parcel-items"
      :parcel="selectedParcel"
      :declaration="selectedDeclaration"
      :shipment="shipment"
      :readonly="!canEdit"
    />
    <replace-data
      id="replace-data"
      :shipment="shipment"
      :declarations="selectedDeclarations"
    />
    <b-row
      align-h="end"
    >
      <b-col
        cols="auto"
      >
        <b-button
          v-if="endpoint === 'needs-attention-ens' && canEditShipment"
          v-b-modal.replace-data
          variant="outline-success"
          class="m-1"
          :disabled="selectedDeclarations.length === 0"
        >
          Replace data
        </b-button>
        <!--This span is required to show tooltip on disabled button. -->
        <span
          v-if="endpoint === 'needs-attention-ens'"
          v-b-tooltip.hover.top="selectedDeclarations.length === 0 ?
            'Please select appropriate filings that you would like to be resent' : ''"
        >
          <b-button
            variant="outline-warning"
            :disabled="selectedDeclarations.length === 0"
            @click="resendEns()"
          >
            Resend
          </b-button>
        </span>

        <template v-if="endpoint === 'registered-ens' && canEditShipment">
          <b-button
            variant="outline-success"
            class="m-1"
            :disabled="excelLoading"
            @click="handleExcelDownload"
          >
            Download as Excel
            <b-spinner
              v-if="excelLoading"
              small
              class="ml-1"
            />
          </b-button>
          <b-button
            variant="outline-success"
            class="m-1"
            :disabled="warehouseExcelLoading"
            @click="handleWarehouseExcelDownload"
          >
            Download Excel for Warehouse
            <b-spinner
              v-if="warehouseExcelLoading"
              small
              class="ml-1"
            />
          </b-button>
        </template>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <special-case-ens
          v-if="endpoint === 'special-case-ens'"
          :shipment="shipment"
          :parcels="parcels"
          :data-loading="loading"
          @sortDesc="fetchData()"
          @update:filters="treeFilters = $event"
        />
        <parcels-tree-ens
          v-else
          :reload-parcels="reloadParcelsKey"
          :shipment="shipment"
          :declarations="declarations"
          filter="cleared"
          clearance="true"
          :data-loading="loading"
          :disable-checkbox="endpoint !== 'needs-attention-ens'"
          @sortDesc="fetchData()"
          @selection="selection"
          @update:filters="treeFilters = $event"
          @edit-parcel="editParcel"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCol,
  BRow,
  BButton,
  VBTooltip, BSpinner,
} from 'bootstrap-vue'
import EditParcelItems from '@/views/shipments/declarations/modals/EditParcelItems.vue'
import { EventBus } from '@core/utils/utils'
import { searchQuery } from '@core/utils/filter'
import excelDownloadMixin from '@/mixins/excelDownloadMixin'
import ParcelsTreeEns from '@/views/shipments/declarations/ParcelsTreeEns.vue'
import handleError from '@/views/components/errorHandler'
import ReplaceData from '@/views/shipments/declarations/modals/ReplaceData.vue'
import ConfirmModal from '@core/components/confirmation/confirmModal.vue'
import SpecialCaseEns from '@/views/shipments/declarations/SpecialCaseEns.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    ConfirmModal,
    BSpinner,
    SpecialCaseEns,
    ReplaceData,
    ParcelsTreeEns,
    EditParcelItems,
    BCol,
    BRow,
    BButton,
  },
  mixins: [excelDownloadMixin],
  props: {
    shipment: {
      type: Object,
      default: () => {
      },
    },
    filters: {
      type: Object,
      default: () => {
      },
    },
    endpoint: {
      type: String,
      required: true,
      default: '',
    },
    declarationType: {
      type: String,
      default: 'h7',
    },
  },
  data() {
    return {
      loading: false,
      page: 1,
      perPage: 100,
      meta: {},
      selectedDeclaration: null,
      selectedParcel: null,
      showType: 'containers',
      selectedAll: false,
      selectedDeclarations: [],
      selectedParcels: [],
      allParcelsData: [],
      sortBy: null,
      isSortDirDesc: null,
      fields: [
        { key: 'actions', label: 'Actions' },
        { key: 'tracking_number', label: 'Tracking code', sortable: true },
        { key: 'container_code', label: 'Container', sortable: true },
        { key: 'lrn', label: 'LRN' },
        { key: 'mrn', label: 'MRN' },
        { key: 'updated_at', label: 'Declaration time' },
      ],
      declarations: [],
      parcels: [],
      editEnabled: false,
      treeFilters: {},
      reloadParcelsKey: 0,
    }
  },
  computed: {
    canEdit() {
      return this.editEnabled && this.$permissions().hasPermission('edit shipment')
    },
    canEditShipment() {
      return this.$permissions().hasPermission('edit shipment')
    },
    url() {
      return `/v1/shipments/${this.shipment.id}/declarations/${this.declarationType}/${this.endpoint}`
    },
    mergedFilters() {
      const merged = { ...(this.filters || {}), ...(this.treeFilters || {}) }
      return Object.fromEntries(
        Object.entries(merged).filter(([, v]) => {
          if (Array.isArray(v)) return v.length > 0
          return v !== '' && v !== null && v !== undefined && v !== false
        }),
      )
    },
    excelDownloadUrl() {
      return `${process.env.VUE_APP_API_URL}/v1/companies/${this.shipment.company.id}/shipments/${this.shipment.id}/declarations/${this.declarationType}/${this.endpoint}/excel`
    },
    warehouseExcelUrl() {
      return `${process.env.VUE_APP_API_URL}/v1/companies/${this.shipment.company.id}/shipments/${this.shipment.id}/declarations/${this.declarationType}/${this.endpoint}/excel-for-warehouse`
    },
  },
  watch: {
    filters() {
      this.fetchData()
    },
    treeFilters() {
      this.fetchData()
    },
    sortBy() {
      this.fetchData()
    },
    isSortDirDesc() {
      this.fetchData()
    },
    showType() {
      this.fetchData()
    },
  },
  created() {
    const q = this.$route.query || {}
    const hasTreeFilters = !!(q.tracking_number || q.container_code || q.mrn || q.lrn)
    if (!hasTreeFilters) {
      this.fetchData()
    }
  },
  mounted() {
    EventBus.$on('general-declaration-update', this.fetchData)
    EventBus.$on('set-view-parcel', this.viewParcel)
    EventBus.$on('set-post-clearance-parcel', this.postClearanceParcel)
    EventBus.$on('pageChange', this.handlePageChange)
    EventBus.$on('renewDeclarationList', this.handleRenewDeclarationList)
  },
  beforeDestroy() {
    EventBus.$off('general-declaration-update', this.fetchData)
    EventBus.$off('set-view-parcel', this.viewParcel)
    EventBus.$off('pageChange', this.handlePageChange)
    EventBus.$off('set-post-clearance-parcel', this.postClearanceParcel)
    EventBus.$off('renewDeclarationList', this.handleRenewDeclarationList)
  },
  methods: {
    handlePageChange(newPage) {
      this.page = newPage
      this.fetchData()
    },
    handleRenewDeclarationList() {
      this.fetchData()
      EventBus.$emit('refresh-statistics')
      this.reloadParcelsKey += 1
    },
    selection(data) {
      this.selectedAll = data.selectedAll
      this.selectedDeclarations = data.selectedDeclarations
      this.selectedParcels = data.selectedParcels
    },
    viewParcel(declaration, parcel) {
      this.editEnabled = false
      this.selectedDeclaration = declaration
      this.selectedParcel = parcel
      this.$nextTick(() => {
        this.$bvModal.show('edit-parcel-items')
      })
    },
    editParcel({ declaration, parcel }) {
      this.editEnabled = true
      this.selectedDeclaration = declaration
      this.selectedParcel = parcel
    },
    fetchData(activeTab, showLoader = true) {
      if ((!activeTab || activeTab === this.endpoint) && !this.loading) {
        if (showLoader) {
          this.loading = true
        }
        this.$http.get(searchQuery(this.url, this.mergedFilters), {
          params: {
            sortBy: this.sortBy,
            sortDesc: this.isSortDirDesc,
          },
        })
          .then(response => {
            if (this.endpoint === 'special-case-ens') {
              this.loading = false
              this.parcels = response.data.data
            } else {
              this.loading = false
              this.declarations = response.data.data
            }
          })
      }
    },
    canDownloadClearanceMessages() {
      return this.shipment.company.config?.canDownloadClearanceMessages
    },
    resendEns() {
      this.loading = true
      this.$http.post(`/v1/shipments/${this.shipment.id}/declarations/ens/resend`, {
        messageType: 'F26',
        declarationIds: this.selectedDeclarations,
      })
        .then(response => {
          this.loading = false
          this.selectedDeclarations = []
          this.fetchData()
          EventBus.$emit('refresh-statistics')
          // Display success message from backend response
          if (response.data && response.data.message) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: response.data.message,
                icon: 'CheckIcon',
                variant: 'success',
              },
            })
          }
        })
        .catch(error => {
          handleError(error, this.$toast)
          this.loading = false
        })
    },
  },
}
</script>
